
import React, { useEffect, useState, useCallback,useRef } from "react";
import { Table } from "react-super-responsive-table";
import { Card, Col, Row } from "reactstrap";
import "../../assets/scss/style.css";
import { getAllProperties } from "services/services";
import { useHistory } from "react-router-dom";
import Pagination, { bootstrap5PaginationPreset } from "react-responsive-pagination";
import DateRangePicker from "react-bootstrap-daterangepicker";
import { CInputGroup, CInputGroupText } from "@coreui/react";
import Schedule from "../../assets/images/Schedule.svg";
import { StandardPickerDateFormat, downloadFile } from "common/utility";
import calendarremovelines from "../../assets/images/calendar-remove-lines.svg";
import { useTranslation, withTranslation } from "react-i18next";
import { dateformat, longStringConvertToShort } from "admin/commonFunction";
import { getCurrentUserRole, getCurrentBrokerOfficeId } from "common/roleConfig";
const Properties = () => {
  const  {t} = useTranslation();
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [listingData, setListingData] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const perPage = 10;
  const [searchName, setSearchName] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [kycStatusFilter, setKycStatusFilter] = useState("");
  const [dateFilter, setDateFilter] = useState([]);
  const [calendarIsShowing, setCalendarIsShowing] = useState(false);
  
  // Simple filters and sorting
  const [dateRangeType, setDateRangeType] = useState("");
  const [sortBy, setSortBy] = useState("");
  const [daysOnMarket, setDaysOnMarket] = useState("");
  const [soldPriceRange, setSoldPriceRange] = useState("");
  const [avgPriceRange, setAvgPriceRange] = useState("");
  const [commissionRange, setCommissionRange] = useState("");
  const [avgSqft, setAvgSqft] = useState("");
  const [minSqft, setMinSqft] = useState("");
  const [maxSqft, setMaxSqft] = useState("");
  const [pricePerSqft, setPricePerSqft] = useState("");
  const [minPricePerSqft, setMinPricePerSqft] = useState("");
  const [maxPriceSqft, setMaxPriceSqft] = useState("");
  const [propertyType, setPropertyType] = useState("");

  const history = useHistory();

  const fetchData = useCallback(
    async (
      pageNumber = currentPage,
      pageSize = perPage,
      search = searchName,
      dateFilterValue = dateFilter,
      status = statusFilter,
      kyc = kycStatusFilter,
      exportrequest = "false",
      propertyTypeFilter = propertyType
    ) => {
      try {
        setLoading(true);
        const [fromDate, toDate] = dateFilterValue || [];

        // Get current user role and ID for role-based filtering
        const userRole = getCurrentUserRole();
        const brokerOfficeId = getCurrentBrokerOfficeId();
        const brokerId = userRole === "2" ? brokerOfficeId : "";

        const res = await getAllProperties(
          pageNumber,
          pageSize,
          search,
          fromDate || "",
          toDate || "",
          status,
          kyc,
          exportrequest,
          true, // analytics = true
          dateRangeType,
          soldPriceRange,
          daysOnMarket,
          sortBy,
          avgPriceRange,
          commissionRange, // This will be mapped to buyerAgentCommission in the API
          minSqft,
          maxSqft,
          minPricePerSqft,
          maxPriceSqft,
          brokerId,
          propertyTypeFilter
        );

        const items = res?.data?.items || [];

        const filteredItems =
          status === "approved"
            ? items.filter((b) => b.status === "approved")
            : status === "draft"
            ? items.filter((b) => b.status === "draft")
            : items;

        setListingData(filteredItems);


        const explicitPages =
          Number(
            res?.data?.totalPages ||
              res?.data?.pageCount ||
              res?.data?.pages ||
              res?.data?.pagination?.totalPages ||
              0
          ) || 0;

        if (explicitPages > 0) {
          setTotalPages(explicitPages);
        } else {
          const totalRecords =
            Number(
              res?.data?.total ||
                res?.data?.totalCount ||
                res?.data?.count ||
                res?.data?.totalRecords ||
                res?.data?.totalListings ||
                res?.data?.pagination?.total ||
                0
            ) || 0;

          let pages = Math.ceil(totalRecords / perPage);
          if (pages < 1 && filteredItems.length > 0) pages = 1;
          setTotalPages(pages);
        }
      } catch (err) {
        console.error(err);
        setListingData([]);
        setTotalPages(0);
      } finally {
        setLoading(false);
      }
    },
    [currentPage, perPage, searchName, dateFilter, statusFilter, kycStatusFilter, dateRangeType, soldPriceRange, daysOnMarket, sortBy, avgPriceRange, commissionRange, minSqft, maxSqft, minPricePerSqft, maxPriceSqft, propertyType]
  );
  

  const handleDetailProperties = (id) => {
    // Pass current page context to help the detail page search more efficiently
    const currentPageContext = {
      page: currentPage,
      search: searchName,
      statusFilter,
      kycStatusFilter,
      dateFilter
    };
    
    history.push(`/admin/property/${id}/details`, { 
      fromPage: currentPage,
      searchContext: currentPageContext 
    });
  };

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Helper function to get filter display labels
  const getActiveFilters = () => {
    const filters = [];
    
    if (searchName) {
      filters.push({ label: "Search", value: searchName });
    }
    if (statusFilter) {
      filters.push({ label: "Status", value: statusFilter === "approved" ? "Approved" : "Draft" });
    }
    if (dateRangeType) {
      const dateRangeLabels = {
        currentYearToDate: "Current Year To Date",
        lastYearToDate: "Last Year To Date",
        currentMonth: "Current Month",
        currentMonthLastYear: "Current Month Last Year",
        startOfAccountToDate: "Start of Account to Date",
        customDateRange: "Custom Date Range"
      };
      filters.push({ label: "Date Range", value: dateRangeLabels[dateRangeType] || dateRangeType });
    }
    if (dateFilter.length === 2) {
      filters.push({ 
        label: "Custom Dates", 
        value: `${StandardPickerDateFormat(dateFilter[0])} - ${StandardPickerDateFormat(dateFilter[1])}` 
      });
    }
    if (sortBy) {
      const sortLabels = {
        activeProperties: "All Properties Active",
        soldProperties: "Properties Sold During Period",
        cancelledListings: "Cancelled Listings During Period",
        streetAddress: "Street Address (A-Z)"
      };
      filters.push({ label: "Sort By", value: sortLabels[sortBy] || sortBy });
    }
    if (daysOnMarket) {
      filters.push({ label: "Days on Market", value: daysOnMarket });
    }
    if (soldPriceRange) {
      const priceLabels = {
        "under-250k": "Under $250K",
        "250k-350k": "$251K to $350K",
        "351k-450k": "$351K to $450K",
        "451k-550k": "$451K to $550K",
        "551k-650k": "$551K to $650K",
        "651k-750k": "$651K to $750K",
        "751k-850k": "$751K to $850K",
        "851k-950k": "$851K to $950K",
        "951k-1m": "$951K to $1M",
        "1m+": "$1M+"
      };
      filters.push({ label: "Avg Price Sold", value: priceLabels[soldPriceRange] || soldPriceRange });
    }
    if (avgPriceRange) {
      const priceLabels = {
        "under-250k": "Under $250K",
        "250k-350k": "$251K to $350K",
        "351k-450k": "$351K to $450K",
        "451k-550k": "$451K to $550K",
        "551k-650k": "$551K to $650K",
        "651k-750k": "$651K to $750K",
        "751k-850k": "$751K to $850K",
        "851k-950k": "$851K to $950K",
        "951k-1m": "$951K to $1M",
        "1m+": "$1M+"
      };
      filters.push({ label: "Avg Price Not Sold", value: priceLabels[avgPriceRange] || avgPriceRange });
    }
    if (commissionRange) {
      filters.push({ label: "Commission", value: commissionRange });
    }
    if (propertyType) {
      const propertyLabels = {
        residential: "Residential",
        commercial: "Commercial",
        land: "Land",
        condo: "Condo",
        townhouse: "Townhouse",
        apartment: "Apartment"
      };
      filters.push({ label: "Property Type", value: propertyLabels[propertyType] || propertyType });
    }
    if (avgSqft) {
      const sqftLabels = {
        "0-1500": "0 to 1,500",
        "1500-3000": "1,500 to 3,000",
        "3000-4500": "3,000 to 4,500",
        "4500+": "4,500+"
      };
      filters.push({ label: "Avg Sqft", value: sqftLabels[avgSqft] || avgSqft });
    }
    if (pricePerSqft) {
      const pricePerSqftLabels = {
        "0-100": "$0 to $100",
        "100-200": "$100 to $200",
        "200-300": "$200 to $300",
        "300-400": "$300 to $400",
        "400+": "$400+"
      };
      filters.push({ label: "Price per Sqft", value: pricePerSqftLabels[pricePerSqft] || pricePerSqft });
    }
    
    return filters;
  };

  const extractPropertyItems = (payload) => {
    if (!payload) {
      return [];
    }
  
    if (Array.isArray(payload?.items)) {
      return payload.items;
    }
  
    if (Array.isArray(payload?.data?.items)) {
      return payload.data.items;
    }
  
    if (Array.isArray(payload?.data)) {
      return payload.data;
    }
  
    if (Array.isArray(payload)) {
      return payload;
    }
  
    return [];
  };
  
  const sanitizeCsvValue = (value) => {
    if (value === null || value === undefined) {
      return '""';
    }
  
    const stringValue = String(value).replace(/"/g, '""');
    return `"${stringValue}"`;
  };
  
  const toCsvRow = (cells = []) => cells.map((cell) => sanitizeCsvValue(cell)).join(",");
  
  const buildFilterMetadataRows = () => {
    const rows = [];
    const activeFilters = getActiveFilters();
    const exportDate = new Date().toLocaleString();
    const generatedBy = getCurrentUserRole() === "1" ? "Admin" : "Broker";
  
    rows.push(toCsvRow(["Export Date", exportDate]));
    rows.push(toCsvRow(["Generated By", generatedBy]));
    rows.push("");
    rows.push(toCsvRow(["Filter Name", "Filter Value"]));
  
    if (activeFilters.length > 0) {
      activeFilters.forEach((filter) => {
        rows.push(toCsvRow([filter.label, filter.value]));
      });
    } else {
      rows.push(toCsvRow(["No filters applied", "All data exported"]));
    }
  
    rows.push("");
    return rows;
  };
  
  const buildCSVFromProperties = (properties = []) => {
    const headers = [
      "Sr No",
      "Listing Type",
      "Listing Agent",
      "Listing Views",
      "Listing Likes",
      "Price Per Sqft",
      "Days on Platform",
      "Date Sold",
      "City",
      "Address",
      "Total Rooms",
      "Square Footage",
      "Price",
      "Commission",
      "Phone",
      "Created At",
      "Status"
    ];
  
    const rows = properties.map((property, index) => {
      const formattedDateSold = property?.dateSold ? dateformat(property.dateSold) : "-";
      const formattedCreatedAt = property?.createdAt ? dateformat(property.createdAt) : "-";
  
      return [
        sanitizeCsvValue(index + 1),
        sanitizeCsvValue(property?.listingType || "-"),
        sanitizeCsvValue(property?.agentId?.name || "-"),
        sanitizeCsvValue(property?.views ?? "-"),
        sanitizeCsvValue(property?.favorites_count ?? "-"),
        sanitizeCsvValue(property?.price_per_sqft ?? "-"),
        sanitizeCsvValue(property?.days_on_market ?? "-"),
        sanitizeCsvValue(formattedDateSold),
        sanitizeCsvValue(property?.city || "-"),
        sanitizeCsvValue(property?.address || "-"),
        sanitizeCsvValue(property?.total_rooms ?? "-"),
        sanitizeCsvValue(property?.property_square_footage ?? "-"),
        sanitizeCsvValue(property?.listing_price ?? "-"),
        sanitizeCsvValue(property?.buyer_agent_commission ?? "-"),
        sanitizeCsvValue(property?.phone || "-"),
        sanitizeCsvValue(formattedCreatedAt),
        sanitizeCsvValue(property?.status || "-"),
      ];
    });
  
    const metadataRows = buildFilterMetadataRows();
    const headerRow = headers.map((header) => sanitizeCsvValue(header)).join(",");
    const propertyRows = rows.map((row) => row.join(","));
    const csvRows = [...metadataRows, headerRow, ...propertyRows];
    return csvRows.join("\n");
  };

  const handleExport = async () => {
    try {
      setExporting(true);
      const [fromDate, toDate] = dateFilter || [];

      // Get current user role and ID for role-based filtering
      const userRole = getCurrentUserRole();
      const brokerOfficeId = getCurrentBrokerOfficeId();
      const brokerId = userRole === "2" ? brokerOfficeId : "";

      const response = await getAllProperties(
        1,
        1000,
        searchName,
        fromDate || "",
        toDate || "",
        statusFilter,
        kycStatusFilter,
        "false",
        true,
        dateRangeType,
        soldPriceRange,
        daysOnMarket,
        sortBy,
        avgPriceRange,
        commissionRange,
        minSqft,
        maxSqft,
        minPricePerSqft,
        maxPriceSqft,
        brokerId,
        propertyType
      );
  
      const payload = response?.data ?? response;
      const properties = extractPropertyItems(payload);
  
      if (!properties.length) {
        console.warn("Export Properties: No records available for the current filters.");
        return;
      }
  
      const csvContent = buildCSVFromProperties(properties);
      const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
      const fileUrl = URL.createObjectURL(blob);
      const timestamp = new Date().toISOString().split("T")[0];
      downloadFile(fileUrl, `properties_${timestamp}.csv`);
      setTimeout(() => URL.revokeObjectURL(fileUrl), 0);
    } catch (err) {
      console.error("Export error:", err);
    } finally {
      setExporting(false);
    }
  };

  const dateFilterChange = (e, picker) => {
    const filter = [
      picker.startDate.format("YYYY-MM-DD"),
      picker.endDate.format("YYYY-MM-DD"),
    ];
    setDateFilter(filter);
    setCurrentPage(1);
    fetchData(1, perPage, searchName, filter, statusFilter, kycStatusFilter);
  };
  const inputRef = useRef(null);

  return (
    <div className="page-content">
      <div className="container-fluid">
        <Row>
          <Col lg="12">
            <Card className="card-transparent">
              {/* Active Filters Row */}
              {getActiveFilters().length > 0 && (
                <Row className="mb-3">
                  <Col lg="12">
                    <div 
                      className="d-flex align-items-center flex-wrap gap-2 p-3" 
                      style={{ 
                        backgroundColor: "#e8f5e9", 
                        borderRadius: "8px",
                        border: "1px solid #c8e6c9",
                        boxShadow: "0 2px 4px rgba(0,0,0,0.05)"
                      }}
                    >
                      <div className="d-flex align-items-center me-3">
                        <i className="bx bx-filter-alt" style={{ fontSize: "18px", color: "#01A54F", marginRight: "8px" }}></i>
                        <strong style={{ color: "#2e7d32", fontSize: "14px" }}>Active Filters:</strong>
                      </div>
                      <div className="d-flex align-items-center flex-wrap gap-2 flex-grow-1">
                        {getActiveFilters().map((filter, index) => (
                          <span 
                            key={index} 
                            className="badge"
                            style={{ 
                              backgroundColor: "#01A54F",
                              color: "#fff",
                              fontSize: "12px", 
                              padding: "6px 14px",
                              borderRadius: "20px",
                              fontWeight: "500",
                              border: "none",
                              boxShadow: "0 2px 4px rgba(1,165,79,0.2)"
                            }}
                          >
                            <span style={{ fontWeight: "600" }}>{filter.label}:</span> {filter.value}
                          </span>
                        ))}
                      </div>
                      <button
                        className="btn btn-sm"
                        onClick={() => {
                          setSearchName("");
                          setStatusFilter("");
                          setKycStatusFilter("");
                          setDateFilter([]);
                          setDateRangeType("");
                          setSortBy("");
                          setDaysOnMarket("");
                          setSoldPriceRange("");
                          setAvgPriceRange("");
                          setCommissionRange("");
                          setAvgSqft("");
                          setMinSqft("");
                          setMaxSqft("");
                          setPricePerSqft("");
                          setMinPricePerSqft("");
                          setMaxPriceSqft("");
                          setPropertyType("");
                          setCurrentPage(1);
                          fetchData(1, perPage, "", [], "", "");
                        }}
                        style={{ 
                          backgroundColor: "#fff",
                          color: "#d32f2f",
                          border: "1px solid #d32f2f",
                          borderRadius: "20px",
                          padding: "6px 16px",
                          fontWeight: "500",
                          fontSize: "12px",
                          transition: "all 0.3s"
                        }}
                        onMouseEnter={(e) => {
                          e.target.style.backgroundColor = "#d32f2f";
                          e.target.style.color = "#fff";
                        }}
                        onMouseLeave={(e) => {
                          e.target.style.backgroundColor = "#fff";
                          e.target.style.color = "#d32f2f";
                        }}
                      >
                        <i className="bx bx-x" style={{ marginRight: "4px" }}></i>
                        Clear All
                      </button>
                    </div>
                  </Col>
                </Row>
              )}

              <Row className="d-flex align-items-center mb-3">
                <Col lg="4">
                  <h5 className="text-color-gredient">Properties</h5>
                </Col>
                <Col lg="8" className="d-flex align-items-center justify-content-end">
                  {/* Date Range Dropdown */}
                  <div className="me-2">
                    <select
                      className="form-control select2"
                      style={{ width: "180px" }}
                      value={dateRangeType}
                      onChange={(e) => {
                        const newDateRangeType = e.target.value;
                        setDateRangeType(newDateRangeType);
                        
                        // Clear date filter when switching away from custom date range
                        if (newDateRangeType !== "customDateRange") {
                          setDateFilter([]);
                        }
                        
                        setCurrentPage(1);
                        // Trigger fetchData with the new date range type
                        fetchData(1, perPage, searchName, [], statusFilter, kycStatusFilter);
                      }}
                    >
                      <option value="">Select Date Range</option>
                      <option value="currentYearToDate">Current Year To Date</option>
                      <option value="lastYearToDate">Last Year To Date</option>
                      <option value="currentMonth">Current Month</option>
                      <option value="currentMonthLastYear">Current Month Last Year</option>
                      <option value="startOfAccountToDate">Start of Account to Date</option>
                      <option value="customDateRange">Custom Date Range</option>
                    </select>
                  </div>

                  {/* Date picker always visible */}
                  <div className="common-filters date-filter me-xxl-2 my-3 my-xxl-0 d-inline-block">
                    <label htmlFor="search-bar-0" className="w-100 m-0">
                      <span id="search-bar-0-label" className="sr-only">
                        Filter by date
                      </span>
                      <CInputGroup className="">
                        <DateRangePicker
                          // initialSettings={{ startDate: '1/1/2014', endDate: '3/1/2014' }}
                          onApply={dateFilterChange}
                          // onCancel={}
                          onShow={() => setCalendarIsShowing(true)}
                          onHide={() => setCalendarIsShowing(false)}
                        >
                          <input
                            ref={inputRef}
                            placeholder={t("Custom Date Range")}
                            className="form-control"
                            readOnly={true}
                            value={
                              dateFilter.length
                                ? `${StandardPickerDateFormat(
                                    dateFilter[0]
                                  )} - ${StandardPickerDateFormat(
                                    dateFilter[1]
                                  )}`
                                : ""
                            }
                          />
                        </DateRangePicker>
                        <CInputGroupText>
                          <img
                            onClick={() => {
                              if (!calendarIsShowing && dateFilter.length) {
                                setDateFilter([]);
                                fetchData(
                                  1,
                                  perPage,
                                  searchName,
                                  [],
                                  "",
                                  ""
                                );
                              }
                            }}
                            src={
                              calendarIsShowing
                                ? calendarremovelines
                                : dateFilter.length
                                ? calendarremovelines
                                : Schedule
                            }
                            alt=""
                            width={20}
                          />
                        </CInputGroupText>
                      </CInputGroup>
                    </label>
                  </div>

                  {/* Search */}
                  <div className="common-filters search-box me-2">
                    <input
                      type="search"
                      className="form-control"
                      placeholder="Search..."
                      value={searchName}
                      onChange={(e) => {
                        setSearchName(e.target.value);
                        setCurrentPage(1);
                      }}
                    />
                  </div>
                  {/* Status Filter */}
                  <div className="me-2">
                    <select
                      className="form-control select2"
                      style={{ width: "165px" }}
                      value={statusFilter}
                      onChange={(e) => {
                        setStatusFilter(e.target.value);
                        setCurrentPage(1);
                      }}
                    >
                      <option value="">All Status</option>
                      <option value="approved">Approved</option>
                      <option value="draft">Draft</option>
                    </select>
                  </div>
                </Col>
              </Row>
              
              {/* Integrated Filters Row */}
              <Row className="mb-3">
                <Col lg="12">
                  <Row>
                    
                    {/* Sort By */}
                    <Col md="2" className="mb-2">
                      <label className="form-label">Sort By</label>
                      <select
                        className="form-control"
                        value={sortBy}
                        onChange={(e) => {
                          setSortBy(e.target.value);
                          setCurrentPage(1);
                        }}
                      >
                        <option value="">Select Sort Option</option>
                        <option value="activeProperties">All Properties Active</option>
                        <option value="soldProperties">Properties Sold During Period</option>
                        <option value="cancelledListings">Cancelled Listings During Period</option>
                       
                        <option value="streetAddress">Street Address (A-Z)</option>
                      </select>
                    </Col>
                    
                    {/* Days on Market */}
                    <Col md="2" className="mb-2">
                      <label className="form-label">Days on Platform</label>
                      <select
                        className="form-control"
                        value={daysOnMarket}
                        onChange={(e) => {
                          setDaysOnMarket(e.target.value);
                          setCurrentPage(1);
                        }}
                      >
                        <option value="">Days on Platform</option>
                        <option value="0-30">0 to 30</option>
                        <option value="31-60">31 to 60</option>
                        <option value="61-90">61 to 90</option>
                        <option value="91-120">91 to 120</option>
                        <option value="120+">120+</option>
                      </select>
                    </Col>
                    
                    {/* Average Price of Home Sold */}
                    <Col md="2" className="mb-2">
                      <label className="form-label">Avg Price Sold</label>
                      <select
                        className="form-control"
                        value={soldPriceRange}
                        onChange={(e) => {
                          setSoldPriceRange(e.target.value);
                          setCurrentPage(1);
                        }}
                      >
                        <option value="">Price Range</option>
                        <option value="under-250k">Under $250K</option>
                        <option value="250k-350k">$251K to $350K</option>
                        <option value="351k-450k">$351K to $450K</option>
                        <option value="451k-550k">$451K to $550K</option>
                        <option value="551k-650k">$551K to $650K</option>
                        <option value="651k-750k">$651K to $750K</option>
                        <option value="751k-850k">$751K to $850K</option>
                        <option value="851k-950k">$851K to $950K</option>
                        <option value="951k-1m">$951K to $1M</option>
                        <option value="1m+">$1M+</option>
                      </select>
                    </Col>
                    
                    {/* Average Price of Home NOT Sold */}
                    <Col md="2" className="mb-2">
                      <label className="form-label">Avg Price Not Sold</label>
                      <select
                        className="form-control"
                        value={avgPriceRange}
                        onChange={(e) => {
                          setAvgPriceRange(e.target.value);
                          setCurrentPage(1);
                        }}
                      >
                        <option value="">Price Range</option>
                        <option value="under-250k">Under $250K</option>
                        <option value="250k-350k">$251K to $350K</option>
                        <option value="351k-450k">$351K to $450K</option>
                        <option value="451k-550k">$451K to $550K</option>
                        <option value="551k-650k">$551K to $650K</option>
                        <option value="651k-750k">$651K to $750K</option>
                        <option value="751k-850k">$751K to $850K</option>
                        <option value="851k-950k">$851K to $950K</option>
                        <option value="951k-1m">$951K to $1M</option>
                        <option value="1m+">$1M+</option>
                      </select>
                    </Col>
                    
                    {/* Buyer Agent Commission */}
                    <Col md="2" className="mb-2">
                      <label className="form-label">Commission</label>
                      <select
                        className="form-control"
                        value={commissionRange}
                        onChange={(e) => {
                          setCommissionRange(e.target.value);
                          setCurrentPage(1);
                        }}
                      >
                        <option value="">Commission</option>
                        <option value="0-1">0% to 1%</option>
                        <option value="1-2">1% to 2%</option>
                        <option value="2-3">2% to 3%</option>
                        <option value="3+">3%+</option>
                      </select>
                    </Col>
                    
                    {/* Property Type */}
                    <Col md="2" className="mb-2">
                      <label className="form-label">Property Type</label>
                      <select
                        className="form-control"
                        value={propertyType}
                        onChange={(e) => {
                          setPropertyType(e.target.value);
                          setCurrentPage(1);
                        }}
                      >
                        <option value="">Property Type</option>
                        <option value="residential">Residential</option>
                        <option value="commercial">Commercial</option>
                        <option value="land">Land</option>
                        <option value="condo">Condo</option>
                        <option value="townhouse">Townhouse</option>
                        <option value="apartment">Apartment</option>
                      </select>
                    </Col>
                  </Row>
                  
                  <Row>
                    {/* Average Sqft */}
                    <Col md="2" className="mb-2">
                      <label className="form-label">Avg Sqft</label>
                      <select
                        className="form-control"
                        value={avgSqft}
                        onChange={(e) => {
                          const value = e.target.value;
                          setAvgSqft(value);
                          
                          // Map the selected range to minSqft and maxSqft
                          if (value === "0-1500") {
                            setMinSqft("0");
                            setMaxSqft("1500");
                          } else if (value === "1500-3000") {
                            setMinSqft("1500");
                            setMaxSqft("3000");
                          } else if (value === "3000-4500") {
                            setMinSqft("3000");
                            setMaxSqft("4500");
                          } else if (value === "4500+") {
                            setMinSqft("4500");
                            setMaxSqft("");
                          } else {
                            setMinSqft("");
                            setMaxSqft("");
                          }
                          
                          setCurrentPage(1);
                        }}
                      >
                        <option value="">Avg Sqft</option>
                        <option value="0-1500">0 to 1,500</option>
                        <option value="1500-3000">1,500 to 3,000</option>
                        <option value="3000-4500">3,000 to 4,500</option>
                        <option value="4500+">4,500+</option>
                      </select>
                    </Col>
                    
                    {/* Price per Sqft */}
                    <Col md="2" className="mb-2">
                      <label className="form-label">Price per Sqft</label>
                      <select
                        className="form-control"
                        value={pricePerSqft}
                        onChange={(e) => {
                          const value = e.target.value;
                          setPricePerSqft(value);
                          
                          // Map the selected range to minPricePerSqft and maxPriceSqft
                          if (value === "0-100") {
                            setMinPricePerSqft("0");
                            setMaxPriceSqft("100");
                          } else if (value === "100-200") {
                            setMinPricePerSqft("100");
                            setMaxPriceSqft("200");
                          } else if (value === "200-300") {
                            setMinPricePerSqft("200");
                            setMaxPriceSqft("300");
                          } else if (value === "300-400") {
                            setMinPricePerSqft("300");
                            setMaxPriceSqft("400");
                          } else if (value === "400+") {
                            setMinPricePerSqft("400");
                            setMaxPriceSqft("");
                          } else {
                            setMinPricePerSqft("");
                            setMaxPriceSqft("");
                          }
                          
                          setCurrentPage(1);
                        }}
                      >
                        <option value="">Price per Sqft</option>
                        <option value="0-100">$0 to $100</option>
                        <option value="100-200">$100 to $200</option>
                        <option value="200-300">$200 to $300</option>
                        <option value="300-400">$300 to $400</option>
                        <option value="400+">$400+</option>
                      </select>
                    </Col>
                    
                    {/* Export Button */}
                    <Col md="auto" className="mb-2 d-flex align-items-end">
                      <button
                        className="btn"
                        onClick={handleExport}
                        disabled={exporting}
                        style={{ 
                          minWidth: "120px",
                          backgroundColor: "#01A54F",
                          color: "#fff",
                          border: "none",
                          borderRadius: "6px",
                          padding: "8px 20px",
                          fontWeight: "500",
                          fontSize: "14px",
                          transition: "all 0.3s",
                          boxShadow: "0 2px 4px rgba(1,165,79,0.3)",
                          opacity: exporting ? 0.7 : 1,
                          cursor: exporting ? "not-allowed" : "pointer",
                          height: "38px"
                        }}
                        onMouseEnter={(e) => {
                          if (!exporting) {
                            e.target.style.backgroundColor = "#028a41";
                            e.target.style.transform = "translateY(-1px)";
                            e.target.style.boxShadow = "0 4px 8px rgba(1,165,79,0.4)";
                          }
                        }}
                        onMouseLeave={(e) => {
                          if (!exporting) {
                            e.target.style.backgroundColor = "#01A54F";
                            e.target.style.transform = "translateY(0)";
                            e.target.style.boxShadow = "0 2px 4px rgba(1,165,79,0.3)";
                          }
                        }}
                      >
                        <i className="bx bx-download" style={{ marginRight: "6px", fontSize: "16px" }}></i>
                        {exporting ? "Exporting..." : "Export CSV"}
                      </button>
                    </Col>
                  </Row>
                </Col>
              </Row>
              <div className="table-responsive">
                <Table
                  id="properties-table"
                  className="table common-table table-nowrap table-bordered text-center align-middle"
                >
                  <thead>
                    <tr>
                      <th scope="col">Sr No</th>
                      <th scope="col">Photo</th>
                      <th scope="col">Listing Type</th>
                      <th scope="col">Listing Agent</th>
                      <th scope="col">Listing Views</th>
                      <th scope="col">Listing Likes</th>
                      <th scope="col">Price Per Sqft</th>
                      <th scope="col">Days on Platform</th>
                      <th scope="col">Date Sold</th>
                      <th scope="col">City</th>
                      <th scope="col">Address</th>
                      <th scope="col">Total Rooms</th>
                      <th scope="col">Square Footage</th>
                      <th scope="col">Price</th>
                      <th scope="col">Commission</th>
                      <th scope="col">Phone</th>
                      <th scope="col">Created At</th>
                      <th scope="col">Status</th>
                      <th scope="col">AI Tokens</th>
                    </tr>
                  </thead>
                  <tbody>
                    {loading ? (
                      <tr>
                        <td colSpan="8" className="text-center">Loading ...</td>
                      </tr>
                    ) : listingData.length > 0 ? (
                      listingData.map((element, index) => (
                        <tr key={element?._id || index} style={{ cursor: "pointer" }} onClick={() => handleDetailProperties(element._id)}>
                          <td>{(currentPage - 1) * perPage + (index + 1)}</td>
                          <td>
                            <img
                              src={
                                Array.isArray(element?.photos) && element.photos.length > 0
                                  ? element.photos[0]
                                  : "/placeholder.png"
                              }
                              alt="Property"
                              style={{
                                width: "100px",
                                height: "100px",
                                objectFit: "cover",
                              }}
                            />
                          </td>
                          <td>{element?.listingType || "-"}</td>
                          <td>{element?.agentId?.name || "-"}</td>
                          <td>{element?.views || "-"}</td>
                          <td>{element?.favorites_count || "-"}</td>
                          <td>{element?.price_per_sqft || "-"}</td>
                          <td>{element?.days_on_market || "-"}</td>
                          <td>{element?.dateSold ? dateformat(element?.dateSold) : "-"}</td>
                          <td>{element?.city || "-"}</td>
                          <td style={{ maxWidth: "150px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={element?.address || "-"}>
                            {element?.address ? longStringConvertToShort(element.address, 20) : "-"}
                          </td>
                          <td>{element?.total_rooms || "-"}</td>
                          <td>{element?.property_square_footage || "-"}</td>
                          <td>{element?.listing_price || "-"}</td>
                          <td>{element?.buyer_agent_commission || "-"}</td>
                          <td>{element?.phone || "-"}</td>
                          <td>{element?.createdAt ? dateformat(element?.createdAt) : "-"}</td>
                          <td>{element?.status || "-"}</td>
                          <td>
                            {element?.aiPropertyDetailsTokenUsage?.totalTokens != null ? (
                              <span
                                style={{
                                  display: "inline-flex",
                                  alignItems: "center",
                                  gap: 5,
                                  backgroundColor: "#e8f5e9",
                                  color: "#2e7d32",
                                  border: "1px solid #c8e6c9",
                                  borderRadius: 20,
                                  padding: "4px 12px",
                                  fontWeight: 600,
                                  fontSize: 13,
                                  whiteSpace: "nowrap"
                                }}
                              >
                                <i className="bx bx-bot" style={{ fontSize: 14 }}></i>
                                {element.aiPropertyDetailsTokenUsage.totalTokens.toLocaleString()}
                              </span>
                            ) : "-"}
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="8" className="text-center">No Records Found</td>
                      </tr>
                    )}
                  </tbody>
                </Table>
              </div>
              {/* Pagination */}
              {totalPages > 1 && (
                <Row className="mt-2">
                  <Col md={9}></Col>
                  <Col md={3}>
                    <Pagination
                      {...bootstrap5PaginationPreset}
                      current={currentPage}
                      total={totalPages}
                      onPageChange={setCurrentPage}
                      className="pagination justify-content-end gap-1"
                    />
                  </Col>
                </Row>
              )}
            </Card>
          </Col>
        </Row>
      </div>
    </div>
  );
};

export default Properties;
